/* ============================================
   ESSENTIAL FRAGRANCES — THEME JS
   Version 2.0
   ============================================ */

'use strict';
// Keep cart actions inside the visitor's selected storefront language.
const EF_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || ((document.documentElement.lang||'en').toLowerCase().startsWith('fr') ? '/fr/' : '/');


// ── UTILITIES ──
const EF = {
  // Query helpers
  qs: (sel, ctx = document) => ctx.querySelector(sel),
  qsa: (sel, ctx = document) => [...ctx.querySelectorAll(sel)],
  on: (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts),

  // Format money
  formatMoney: (cents) => {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  },

  // Dispatch custom event
  emit: (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`ef:${name}`, { detail, bubbles: true }));
  },

  // Debounce
  debounce: (fn, ms = 200) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  }
};

// ── HEADER SCROLL ──
(function initHeader() {
  const header = EF.qs('#SiteHeader');
  const announcement = EF.qs('.announcement-bar');
  if (!header) return;

  const onScroll = EF.debounce(() => {
    const scrolled = window.scrollY > 50;
    header.classList.toggle('is-scrolled', scrolled);
    if (announcement && window.innerWidth > 768) {
      announcement.style.transform = scrolled ? 'translateY(-100%)' : '';
      announcement.style.transition = 'transform 0.4s ease';
    }
  }, 10);

  window.addEventListener('scroll', onScroll, { passive: true });
})();

// ── MOBILE MENU ──
(function initMobileMenu() {
  const toggle = EF.qs('#MenuToggle');
  const menu = EF.qs('#MobileMenu');
  const close = EF.qs('#MobileMenuClose');
  const overlay = EF.qs('#MobileMenuOverlay');
  if (!menu) return;

  const open = () => {
    menu.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');
    toggle?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  };
  const closeMenu = () => {
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    toggle?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  };

  EF.on(toggle, 'click', open);
  EF.on(close, 'click', closeMenu);
  EF.on(overlay, 'click', closeMenu);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menu.classList.contains('is-open')) closeMenu();
  });
})();

// ── CART DRAWER ──
(function initCartDrawer() {
  const drawer = EF.qs('#CartDrawer');
  const overlay = EF.qs('#CartOverlay');
  const toggle = EF.qs('#CartToggle');
  const closeBtn = EF.qs('#CartClose');
  if (!drawer) return;

  window.openCart = () => {
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    EF.emit('cart:open');
  };

  window.closeCart = () => {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    EF.emit('cart:close');
  };

  EF.on(toggle, 'click', openCart);
  EF.on(closeBtn, 'click', closeCart);
  EF.on(overlay, 'click', closeCart);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeCart();
  });

  // Update cart count
  window.updateCartCount = (count) => {
    const badge = EF.qs('#CartCount');
    if (!badge) return;
    badge.textContent = count;
    badge.classList.toggle('is-hidden', count === 0);
  };

  // Quantity buttons
  EF.on(document, 'click', async (e) => {
    const btn = e.target.closest('.qty-btn');
    if (!btn) return;

    const line = parseInt(btn.dataset.line);
    const action = btn.dataset.action;
    const qtyEl = btn.closest('.cart-item')?.querySelector('.qty-value');
    if (!qtyEl) return;

    let qty = parseInt(qtyEl.textContent) + (action === 'plus' ? 1 : -1);
    qty = Math.max(0, qty);

    try {
      const res = await fetch(EF_ROOT + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity: qty })
      });
      const cart = await res.json();
      await refreshCartDrawer();
      updateCartCount(cart.item_count);
      const totalEl = EF.qs('#CartTotal'); if (totalEl) totalEl.textContent = EF.formatMoney(cart.total_price);
    } catch (err) {
      console.error('Cart update failed:', err);
    }
  });

  // Remove item
  EF.on(document, 'click', async (e) => {
    const btn = e.target.closest('.cart-item__remove');
    if (!btn) return;
    const line = parseInt(btn.dataset.line);
    try {
      const res = await fetch(EF_ROOT + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity: 0 })
      });
      const cart = await res.json();
      await refreshCartDrawer();
      updateCartCount(cart.item_count);
    } catch (err) {
      console.error('Remove failed:', err);
    }
  });
})();

// ── REFRESH CART DRAWER ──
// NOTE: Section key must match the filename (cart-side.liquid), NOT the schema name.
async function refreshCartDrawer() {
  try {
    const res = await fetch(EF_ROOT + '?sections=cart-side');
    const data = await res.json();
    if (data['cart-side']) {
      const temp = document.createElement('div');
      temp.innerHTML = data['cart-side'];
      const newBody = temp.querySelector('#CartBody');
      const newFooter = temp.querySelector('.cart-drawer__footer');
      if (newBody) EF.qs('#CartBody').innerHTML = newBody.innerHTML;
      if (newFooter) EF.qs('.cart-drawer__footer').innerHTML = newFooter.innerHTML;
    }
  } catch (e) {
    // Fallback: reload page
  }
}

// Shopify checkout: submit a genuine cart form, preserving the selected locale.
window.EFProceedToCheckout = () => {
  const form = document.createElement('form');
  form.method = 'post';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  form.action = root + 'cart';
  form.style.display = 'none';
  const checkout = document.createElement('button');
  checkout.type = 'submit';
  checkout.name = 'checkout';
  checkout.value = 'checkout';
  form.appendChild(checkout);
  document.body.appendChild(form);
  if (form.requestSubmit) form.requestSubmit(checkout);
  else checkout.click();
};

// ── ADD TO CART (Quick Add + Product Form) ──
(function initAddToCart() {
  EF.on(document, 'click', async (e) => {
    // Quick add from product grid (regular products — no selling plan)
    const quickBtn = e.target.closest('.product-card__quick-add[data-variant-id]');
    if (quickBtn) {
      e.preventDefault();
      const variantId = quickBtn.dataset.variantId;
      await addToCart(variantId, 1, null, quickBtn);
      return;
    }
  });

  // Product form submit (regular products only — subscription block uses its
  // own inline efSubAddToCart handler in product.liquid and has no <form>).
  // NOTE: the rendered form id is RegularProductForm (see {% form 'product' %}).
  const productForm = EF.qs('#RegularProductForm') || EF.qs('#ProductForm');
  if (productForm) {
    EF.on(productForm, 'submit', async (e) => {
      e.preventDefault();

      // Support regular (#VariantId) and subscription (#SubVariantId) forms
      const variantInput = EF.qs('#SubVariantId') || EF.qs('#VariantId') || EF.qs('#SelectedVariantId');
      if (!variantInput || !variantInput.value) return;
      const variantId = variantInput.value;

      // Read selling_plan if present (subscription products only)
      const sellingPlanInput = EF.qs('#SellingPlanId');
      const sellingPlan = sellingPlanInput && sellingPlanInput.value ? sellingPlanInput.value : null;

      const btn = EF.qs('#AddToCartBtn') || EF.qs('#SubAddBtn');
      const spinner = btn ? btn.querySelector('.btn__spinner') : null;

      if (btn) btn.disabled = true;
      if (spinner) spinner.hidden = false;

      await addToCart(variantId, 1, sellingPlan, btn);

      if (btn) btn.disabled = false;
      if (spinner) spinner.hidden = true;
    });
  }

  /**
   * addToCart
   * @param {string|number} variantId   - the variant ID
   * @param {number}        qty         - quantity
   * @param {string|null}   sellingPlan - selling_plan ID (null for regular products)
   * @param {Element|null}  btnEl       - triggering button (UX feedback only)
   */
  async function addToCart(variantId, qty = 1, sellingPlan = null, btnEl) {
    try {
      const payload = {
        id: parseInt(variantId, 10),
        quantity: qty
      };

      // IMPORTANT: Only include selling_plan when non-empty.
      // Passing an empty string causes Shopify to return 422 or ignore the plan.
      if (sellingPlan && String(sellingPlan).trim() !== '') {
        payload.selling_plan = parseInt(sellingPlan, 10);
      }

      const res = await fetch(EF_ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        console.error('Add to cart failed:', errData);
        window.location = EF_ROOT + 'cart';
        return;
      }

      const cartRes = await fetch(EF_ROOT + 'cart.js');
      const cart = await cartRes.json();

      updateCartCount(cart.item_count);
      await refreshCartDrawer();
      openCart();

      EF.emit('cart:item-added', { cart });
    } catch (err) {
      console.error('Add to cart error:', err);
      window.location = EF_ROOT + 'cart';
    }
  }

  // Expose for inline onclick handlers in product.liquid
  window.EF_addToCart = addToCart;
})();

// ── VARIANT SELECTION ──
(function initVariants() {
  EF.on(document, 'click', (e) => {
    const btn = e.target.closest('.variant-btn');
    if (!btn) return;

    const container = btn.closest('.product-info__variants');
    if (!container) return;

    container.querySelectorAll('.variant-btn').forEach(b => b.classList.remove('is-selected'));
    btn.classList.add('is-selected');

    const variantId = btn.dataset.variantId;
    const price = btn.dataset.price;
    const variantInput = EF.qs('#SelectedVariantId');
    if (variantInput) variantInput.value = variantId;

    if (price) {
      const priceEl = EF.qs('#ProductPrice');
      const addText = EF.qs('#AddToCartText');
      if (priceEl) priceEl.textContent = price;
      if (addText) addText.textContent = `Add to Cart — ${price}`;
    }
  });
})();

// ── PRODUCT GALLERY THUMBS ──
(function initGallery() {
  EF.on(document, 'click', (e) => {
    const thumb = e.target.closest('.product-gallery__thumb');
    if (!thumb) return;

    const mainImg = EF.qs('#ProductMainImg');
    const newSrc = thumb.dataset.src;
    if (mainImg && newSrc) {
      mainImg.src = newSrc;
      document.querySelectorAll('.product-gallery__thumb').forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    }
  });
})();

// ── COUNTDOWN TIMER ──
(function initTimer() {
  const display = EF.qs('#heroTimer');
  if (!display) return;

  // Use session storage to persist timer across page loads
  let endTime = sessionStorage.getItem('ef_timer_end');
  if (!endTime) {
    endTime = Date.now() + (23 * 3600 + 59 * 60 + 47) * 1000;
    sessionStorage.setItem('ef_timer_end', endTime);
  }
  endTime = parseInt(endTime);

  function tick() {
    const diff = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
    const h = String(Math.floor(diff / 3600)).padStart(2, '0');
    const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
    const s = String(diff % 60).padStart(2, '0');

    EF.qsa('#heroTimer, #timerDisplay, #timerDisplay2').forEach(el => {
      if (el) el.textContent = `${h}:${m}:${s}`;
    });

    if (diff > 0) setTimeout(tick, 1000);
  }
  tick();
})();

// ── EMAIL POPUP: real Shopify customer form, respect dismissals ──
(function initPopup() {
  const overlay = document.getElementById('EmailPopupOverlay');
  if (!overlay) return;
  const close = () => {
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    try { sessionStorage.setItem('ef_popup_shown', '1'); } catch(e) {}
  };
  document.getElementById('PopupClose')?.addEventListener('click', close);
  document.getElementById('PopupNoThanks')?.addEventListener('click', close);
  overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
  document.addEventListener('keydown', e => { if(e.key === 'Escape' && overlay.classList.contains('is-open')) close(); });
  let dismissed = false;
  try { dismissed = sessionStorage.getItem('ef_popup_shown') === '1'; } catch(e) {}
  if (dismissed) return;
  const delay = Math.max(2,Math.min(15,Number(overlay.dataset.delay)||12))*1000;
  setTimeout(() => {
    if (overlay.classList.contains('is-open')) return;
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden','false');
  }, delay);
})();

// ── TESTIMONIALS SLIDER ──
(function initTestimonials() {
  const track = EF.qs('#TestiTrack');
  const prev = EF.qs('#TestiPrev');
  const next = EF.qs('#TestiNext');
  if (!track) return;

  let idx = 0;

  function getVisible() {
    if (window.innerWidth >= 1200) return 3;
    if (window.innerWidth >= 768) return 2;
    return 1;
  }

  function slide(dir) {
    const cards = track.querySelectorAll('.testimonial-card');
    const max = Math.max(0, cards.length - getVisible());
    idx = Math.max(0, Math.min(max, idx + dir));
    const cardWidth = cards[0] ? cards[0].offsetWidth + 20 : 380;
    track.style.transform = `translateX(-${idx * cardWidth}px)`;
  }

  EF.on(prev, 'click', () => slide(-1));
  EF.on(next, 'click', () => slide(1));

  // Auto-play
  let autoPlay = setInterval(() => slide(1), 5000);
  track.addEventListener('mouseenter', () => clearInterval(autoPlay));
  track.addEventListener('mouseleave', () => { autoPlay = setInterval(() => slide(1), 5000); });
})();

// ── BRAND TABS (Collection page) ──
(function initBrandTabs() {
  EF.on(document, 'click', (e) => {
    const tab = e.target.closest('.brand-tab');
    if (!tab) return;

    EF.qsa('.brand-tab').forEach(t => t.classList.remove('is-active'));
    tab.classList.add('is-active');

    const filter = tab.dataset.filter;
    const cards = EF.qsa('.product-card');
    cards.forEach(card => {
      if (filter === 'all') {
        card.style.display = '';
        return;
      }
      const brand = card.querySelector('.product-card__brand')?.textContent?.toLowerCase().replace(/\s/g,'-');
      card.style.display = brand?.includes(filter) ? '' : 'none';
    });
  });
})();

// ── FADE IN OBSERVER ──
(function initFadeIn() {
  if (!window.IntersectionObserver) return;

  const obs = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('is-visible');
        obs.unobserve(e.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  EF.qsa('.fade-in').forEach(el => obs.observe(el));
})();

// ── STICKY FILTERS ──
(function initStickyFilters() {
  const bar = EF.qs('#FiltersBar');
  if (!bar) return;

  const onScroll = EF.debounce(() => {
    bar.classList.toggle('is-stuck', window.scrollY > 300);
  }, 20);

  window.addEventListener('scroll', onScroll, { passive: true });
})();

// ── PRODUCT IMAGE ZOOM (hover) ──
(function initImageHover() {
  const mainImg = EF.qs('#ProductMainImg');
  const galleryMain = EF.qs('.product-gallery__main');
  if (!mainImg || !galleryMain) return;

  let isZoomed = false;

  galleryMain.addEventListener('click', () => {
    isZoomed = !isZoomed;
    mainImg.style.transform = isZoomed ? 'scale(1.5)' : '';
    mainImg.style.cursor = isZoomed ? 'zoom-out' : 'zoom-in';
    mainImg.style.transformOrigin = 'center center';
    mainImg.style.transition = 'transform 0.4s ease';
  });
})();

// ── SORT SELECT ──
EF.on(EF.qs('#FilterSort'), 'change', function() {
  const url = new URL(window.location);
  url.searchParams.set('sort_by', this.value);
  window.location = url.toString();
});

// ── BACK TO TOP ──
(function initBackToTop() {
  const btn = document.createElement('button');
  btn.className = 'back-to-top';
  btn.innerHTML = '↑';
  btn.setAttribute('aria-label', 'Back to top');
  btn.style.cssText = `
    position:fixed;bottom:28px;right:28px;width:44px;height:44px;
    background:var(--gold);color:var(--black);border:none;font-size:18px;
    cursor:pointer;z-index:200;opacity:0;transition:opacity 0.3s;
    display:flex;align-items:center;justify-content:center;
  `;
  document.body.appendChild(btn);

  window.addEventListener('scroll', EF.debounce(() => {
    btn.style.opacity = window.scrollY > 600 ? '1' : '0';
    btn.style.pointerEvents = window.scrollY > 600 ? 'all' : 'none';
  }, 50), { passive: true });

  EF.on(btn, 'click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
})();

// ── SMOOTH ANCHOR LINKS ──
EF.on(document, 'click', (e) => {
  const link = e.target.closest('a[href^="#"]');
  if (!link) return;
  const target = EF.qs(link.getAttribute('href'));
  if (!target) return;
  e.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

// ── ANNOUNCEMENT BAR CLOSE ──
EF.on(EF.qs('.announcement-bar'), 'dblclick', function() {
  this.style.display = 'none';
  document.documentElement.style.setProperty('--announcement-height', '0px');
});
